import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, withModifiers as _withModifiers, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "p-5 border-b border-gray-700" }
const _hoisted_2 = {
  id: "confirm-modal-title",
  class: "text-lg font-semibold text-white"
}
const _hoisted_3 = {
  key: 0,
  class: "text-sm text-gray-300 mt-2 whitespace-pre-line"
}
const _hoisted_4 = {
  key: 0,
  class: "px-5 py-3 text-xs text-gray-400 space-y-1"
}
const _hoisted_5 = { class: "flex justify-end gap-2 px-5 py-4 bg-black/20 border-t border-gray-700" }

import { computed, nextTick, onMounted, onBeforeUnmount, ref, watch } from "vue";
	import { useConfirm } from "@/composable/useConfirm";

	
export default /*#__PURE__*/_defineComponent({
  __name: 'ConfirmModal',
  setup(__props) {

	/**
	 * Modal global de confirmation — singleton monté UNE fois dans
	 * App.vue, piloté par le composable `useConfirm`.
	 *
	 * Charte alignée sur le modal "Appliquer le preset" existant dans
	 * FeatureFlagsManagement.vue (fond bg-card, border gray-700, footer
	 * en bg-black/20, escape/click-outside = annulation).
	 */
	const { state, accept, cancel } = useConfirm();

	// Focus trap : on garde la référence du focus parent pour le rendre
	// au moment de la fermeture, et on contraint Tab à boucler entre
	// les boutons cancel/confirm du modal.
	const dialogRef = ref<HTMLElement | null>(null);
	const confirmBtnRef = ref<HTMLElement | null>(null);
	let lastActiveBefore: HTMLElement | null = null;

	const toneClasses = computed(() => {
		switch (state.value.tone) {
			case "danger":
				return "bg-red-700 hover:bg-red-800 text-white";
			case "warning":
				return "bg-yellow-600 hover:bg-yellow-700 text-white";
			case "primary":
			default:
				return "bg-[#219152] hover:bg-[#167841] text-white";
		}
	});

	// Escape → annulation. Tab/Shift+Tab → trap dans les 2 boutons du
	// modal (bouton "Annuler" et bouton "Confirmer/Action"), pas d'évasion
	// vers la page derrière. C'est le minimum WCAG pour une modale
	// destructive (l'utilisateur doit pouvoir choisir Cancel sans risque
	// d'activer un bouton invisible du contenu sous-jacent).
	const onKey = (e: KeyboardEvent) => {
		if (!state.value.open) return;
		if (e.key === "Escape") {
			e.stopPropagation();
			cancel();
			return;
		}
		if (e.key !== "Tab" || !dialogRef.value) return;

		const focusables = Array.from(
			dialogRef.value.querySelectorAll<HTMLElement>(
				'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
			)
		);
		if (focusables.length === 0) return;
		const first = focusables[0];
		const last = focusables[focusables.length - 1];
		const current = document.activeElement as HTMLElement | null;

		if (e.shiftKey && current === first) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && current === last) {
			e.preventDefault();
			first.focus();
		}
	};
	onMounted(() => document.addEventListener("keydown", onKey));
	onBeforeUnmount(() => document.removeEventListener("keydown", onKey));

	// À l'ouverture : mémoriser le focus parent (typiquement le bouton
	// "Supprimer" du tableau qui a déclenché le confirm) pour le rendre
	// à la fermeture. UX clavier : on revient pile où on était.
	watch(
		() => state.value.open,
		async (isOpen) => {
			if (isOpen) {
				lastActiveBefore = document.activeElement as HTMLElement | null;
				await nextTick();
				confirmBtnRef.value?.focus();
			} else if (lastActiveBefore) {
				lastActiveBefore.focus();
				lastActiveBefore = null;
			}
		},
	);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    (_unref(state).open)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 backdrop-blur-sm",
          onClick: _cache[2] || (_cache[2] = _withModifiers(
//@ts-ignore
(...args) => (_unref(cancel) && _unref(cancel)(...args)), ["self"]))
        }, [
          _createElementVNode("div", {
            ref_key: "dialogRef",
            ref: dialogRef,
            class: "bg-card rounded-lg shadow-xl border border-gray-700 max-w-md w-full mx-4 overflow-hidden",
            role: "dialog",
            "aria-modal": "true",
            "aria-labelledby": "confirm-modal-title"
          }, [
            _createElementVNode("div", _hoisted_1, [
              _createElementVNode("h3", _hoisted_2, _toDisplayString(_unref(state).title), 1),
              (_unref(state).message)
                ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(_unref(state).message), 1))
                : _createCommentVNode("", true)
            ]),
            (_unref(state).details && _unref(state).details.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(state).details, (line, i) => {
                    return (_openBlock(), _createElementBlock("p", { key: i }, _toDisplayString(line), 1))
                  }), 128))
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("button", {
                type: "button",
                class: "px-4 py-2 text-sm rounded-md bg-gray-700 hover:bg-gray-600 text-white focus:outline-none",
                onClick: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(cancel) && _unref(cancel)(...args)))
              }, _toDisplayString(_unref(state).cancelLabel), 1),
              _createElementVNode("button", {
                ref_key: "confirmBtnRef",
                ref: confirmBtnRef,
                type: "button",
                class: _normalizeClass(["px-4 py-2 text-sm rounded-md focus:outline-none focus:ring-2", toneClasses.value]),
                onClick: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(accept) && _unref(accept)(...args)))
              }, _toDisplayString(_unref(state).confirmLabel), 3)
            ])
          ], 512)
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})